/**
 * PipelineModelsTable — the pipeline board: sticky filter bar over seven
 * cost-center groups of collapsible rows.
 *
 * The container owns only collection-level concerns — loading skeletons,
 * error/retry, empty, filtering, and group collapse — and forwards every
 * per-row intent (set active / set default / use default / revert / history) up
 * to the page, which owns the production-confirm + safety-gate orchestration.
 *
 * Filtering never uses the page-level EmptyState: an empty result means "your
 * filter matched nothing", not "the engine has no pipelines", and conflating
 * those two reads as an outage.
 *
 * A filter that matches rows inside a COLLAPSED group force-expands that group;
 * otherwise searching would silently return nothing.
 */

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Box, Button, Alert, Skeleton, Paper, TextField, Chip, Typography, InputAdornment } from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import EmptyState from '../../common/EmptyState';
import { colors, typography, zIndex } from '../../../theme/designTokens';
import { ModelCatalogRow, PipelineModel, PipelineModelRef } from '../../../services/quillAdminService';
import PipelineRow from './PipelineRow';
import PipelineGroup from './PipelineGroup';
import {
  COST_CENTER_ORDER,
  groupPipelines,
  readGroupExpanded,
  writeGroupExpanded,
} from './pipelineGroups';
import { pipelineMatchesSearch, pipelineNeedsAttention } from './pipelineRowModel';

/** Height reserved for the sticky filter bar, so group headers stack under it. */
const FILTER_BAR_STICKY_OFFSET = 116;

type StateFilter = 'all' | 'overridden' | 'attention';

const STATE_FILTERS: { key: StateFilter; label: string }[] = [
  { key: 'all', label: 'All' },
  { key: 'overridden', label: 'Overridden' },
  { key: 'attention', label: 'Needs attention' },
];

const isOverridden = (p: PipelineModel): boolean => p.category !== 'fixed' && !p.is_on_default;

export interface PipelineModelsTableProps {
  pipelines: PipelineModel[];
  catalog: ModelCatalogRow[];
  loading: boolean;
  error: string | null;
  onRetry: () => void;
  /** Per-pipeline_key expansion, owned by the page so a refetch cannot reset it. */
  expandedRows: Record<string, boolean>;
  onToggleRow: (pipelineKey: string) => void;
  onSetActive: (pipeline: PipelineModel, ref: PipelineModelRef) => void;
  onSetDefault: (pipeline: PipelineModel, ref: PipelineModelRef) => void;
  onUseDefault: (pipeline: PipelineModel) => void;
  onRevert: (pipeline: PipelineModel) => void;
  onHistory: (pipeline: PipelineModel) => void;
}

const RowSkeleton: React.FC = () => (
  <Paper variant="outlined" sx={{ borderRadius: '16px', p: 1.5 }}>
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
      <Skeleton variant="text" width="24%" height={20} />
      <Skeleton variant="text" width="18%" height={16} />
      <Box sx={{ flexGrow: 1 }} />
      <Skeleton variant="rounded" width={90} height={22} />
      <Skeleton variant="rounded" width={130} height={16} />
    </Box>
  </Paper>
);

export const PipelineModelsTable: React.FC<PipelineModelsTableProps> = ({
  pipelines,
  catalog,
  loading,
  error,
  onRetry,
  expandedRows,
  onToggleRow,
  onSetActive,
  onSetDefault,
  onUseDefault,
  onRevert,
  onHistory,
}) => {
  // ── Filters ──────────────────────────────────────────────────────────
  const [search, setSearch] = useState('');
  const [selectedCostCenters, setSelectedCostCenters] = useState<Set<string>>(new Set());
  const [stateFilter, setStateFilter] = useState<StateFilter>('all');

  // ── Group collapse (persisted, DEFAULT EXPANDED) ─────────────────────
  const [groupExpanded, setGroupExpanded] = useState<Record<string, boolean>>(() => readGroupExpanded());
  useEffect(() => {
    writeGroupExpanded(groupExpanded);
  }, [groupExpanded]);

  const toggleGroup = useCallback((key: string) => {
    setGroupExpanded((prev) => ({ ...prev, [key]: prev[key] === false }));
  }, []);

  // Chip options are derived from the pipelines actually present, ordered by the
  // canonical cost-center list — a newly seeded center appears with no code change.
  const costCenterOptions = useMemo(() => {
    const present = new Set(pipelines.map((p) => p.cost_center).filter(Boolean));
    const ordered = COST_CENTER_ORDER.filter((cc) => present.has(cc));
    Array.from(present)
      .sort()
      .forEach((cc) => {
        if (!ordered.includes(cc)) ordered.push(cc);
      });
    return ordered;
  }, [pipelines]);

  const toggleCostCenter = useCallback((cc: string) => {
    setSelectedCostCenters((prev) => {
      const next = new Set(prev);
      if (next.has(cc)) next.delete(cc);
      else next.add(cc);
      return next;
    });
  }, []);

  const filterActive =
    search.trim().length > 0 || selectedCostCenters.size > 0 || stateFilter !== 'all';

  const visiblePipelines = useMemo(
    () =>
      pipelines.filter((p) => {
        if (!pipelineMatchesSearch(p, search)) return false;
        if (selectedCostCenters.size > 0 && !selectedCostCenters.has(p.cost_center)) return false;
        if (stateFilter === 'overridden' && !isOverridden(p)) return false;
        if (stateFilter === 'attention' && !pipelineNeedsAttention(p, catalog)) return false;
        return true;
      }),
    [pipelines, search, selectedCostCenters, stateFilter, catalog]
  );

  const groups = useMemo(() => groupPipelines(visiblePipelines), [visiblePipelines]);

  if (error) {
    return (
      <Alert
        severity="error"
        action={
          <Button color="inherit" size="small" onClick={onRetry}>
            Retry
          </Button>
        }
      >
        {error}
      </Alert>
    );
  }

  if (loading) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
        {Array.from({ length: 6 }).map((_, i) => (
          <RowSkeleton key={i} />
        ))}
      </Box>
    );
  }

  if (pipelines.length === 0) {
    return (
      <EmptyState
        icon="no_data"
        title="No pipelines defined yet"
        description="Once the Quill engine registers its pipelines, they will appear here for model assignment."
      />
    );
  }

  const filterLabelSx = {
    fontWeight: 700,
    textTransform: 'uppercase' as const,
    letterSpacing: '0.5px',
    color: colors.gray[600],
    minWidth: 84,
  };

  return (
    <Box>
      {/* Sticky filter bar */}
      <Box
        sx={{
          position: 'sticky',
          top: 0,
          zIndex: zIndex.sticky,
          backgroundColor: colors.white,
          border: `1px solid ${colors.gray[200]}`,
          borderRadius: '16px',
          p: 1.5,
          mb: 2,
          display: 'flex',
          flexDirection: 'column',
          gap: 1.25,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
          <TextField
            size="small"
            placeholder="Search name or key…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            inputProps={{ 'aria-label': 'Search pipelines' }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" sx={{ color: 'text.disabled' }} />
                </InputAdornment>
              ),
            }}
            sx={{ width: { xs: '100%', sm: 260 } }}
          />
          {STATE_FILTERS.map((f) => (
            <Chip
              key={f.key}
              label={f.label}
              size="small"
              onClick={() => setStateFilter(f.key)}
              color={stateFilter === f.key ? 'primary' : 'default'}
              variant={stateFilter === f.key ? 'filled' : 'outlined'}
              sx={{ fontWeight: 600 }}
            />
          ))}
          <Box sx={{ flexGrow: 1 }} />
          <Typography variant="caption" sx={{ color: 'text.secondary' }}>
            {visiblePipelines.length} of {pipelines.length}
          </Typography>
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap' }}>
          <Typography variant="caption" sx={filterLabelSx}>
            Cost center
          </Typography>
          <Chip
            label="All"
            size="small"
            onClick={() => setSelectedCostCenters(new Set())}
            color={selectedCostCenters.size === 0 ? 'primary' : 'default'}
            variant={selectedCostCenters.size === 0 ? 'filled' : 'outlined'}
            sx={{ fontWeight: 600 }}
          />
          {costCenterOptions.map((cc) => {
            const on = selectedCostCenters.has(cc);
            return (
              <Chip
                key={cc}
                // The RAW key (AICostsPage precedent) — the friendly title is the
                // group header's job, and duplicating it here would make the two
                // indistinguishable on the page.
                label={cc}
                size="small"
                onClick={() => toggleCostCenter(cc)}
                color={on ? 'primary' : 'default'}
                variant={on ? 'filled' : 'outlined'}
                sx={{ fontFamily: typography.fonts.mono }}
              />
            );
          })}
        </Box>
      </Box>

      {/* Groups */}
      {groups.length === 0 ? (
        <Typography
          sx={{
            fontFamily: typography.fonts.body,
            fontSize: typography.sizes.sm,
            color: 'text.secondary',
            px: 1.5,
            py: 2,
          }}
        >
          No pipelines match these filters.
        </Typography>
      ) : (
        groups.map((group) => {
          // A filtered-in group is always open — otherwise a search would appear
          // to return nothing just because the group was collapsed earlier.
          const expanded = filterActive ? true : groupExpanded[group.key] !== false;
          return (
            <PipelineGroup
              key={group.key}
              title={group.title}
              count={group.pipelines.length}
              overriddenCount={group.pipelines.filter(isOverridden).length}
              expanded={expanded}
              onToggle={() => toggleGroup(group.key)}
              stickyTop={FILTER_BAR_STICKY_OFFSET}
            >
              {group.pipelines.map((p) => (
                <PipelineRow
                  key={p.pipeline_key}
                  pipeline={p}
                  catalog={catalog}
                  expanded={expandedRows[p.pipeline_key] === true}
                  onToggleExpanded={onToggleRow}
                  onSetActive={onSetActive}
                  onSetDefault={onSetDefault}
                  onUseDefault={onUseDefault}
                  onRevert={onRevert}
                  onHistory={onHistory}
                />
              ))}
            </PipelineGroup>
          );
        })
      )}
    </Box>
  );
};

export default PipelineModelsTable;
